/* globals chrome */

import { useState, useEffect, useId } from "react";
import "./TestResultsWrapper.css";
import { SelectionMenu } from "./student-selection/SelectionMenu";
import { storage } from "../../helpers/storage";
import apiClient from "../../helpers/api-client";
import StudentsIcon from "./Resources/students-icon.svg?react";
import SevereFlag from "./Resources/severe-flag.svg?react";
import MajorFlag from "./Resources/major-flag.svg?react";
import MinorFlag from "./Resources/minor-flag.svg?react";
import { intIdToUuid } from "../../helpers/uuid";
import { Box, Grid, Typography } from "@mui/material";
import {
	CoreButton,
	CoreCard,
	CoreModal,
	CoreSearchField,
	CoreSearchFieldPaper,
	CoreTable,
	CoreTableColumn,
	CoreTableRow,
} from "@verity/components/core";
import NoSelectionIcon from "../images/no-course-selected-img.svg?react";
import VisualizerBanner from "../../portal/components/visualizer-banner";
import ProctoringResultView, { ProctoringViewTabsProvider } from "./student-selection/videoplayer/ProctoringResultView";
import { usePortalStateContext } from "../../portal/context";
import i18n from "../../i18n";
import { SummaryValueCard, AssessmentCompletionChart, AssessmentSeverityChart } from "../../reports/shared/charts";
import { registerPlugin } from "../../reports/shared/helpers";
import { ExportReportButton } from "../../reports/shared/report-components";
import { UserRowData } from "../../types/general-types";
import { dispatchProxyMessage } from "@verity/extension/proxy-message/external";

export type SessionInfo = Exclude<
	Awaited<ReturnType<typeof apiClient.proctoringSession.getSessionList>>,
	null | undefined
>[0];
export type UserInfo = SessionInfo["userInfo"];

export type CourseInfoSummary = {
	id?: number | null;
	name?: string | null;
};

export type LiveSessionNote = {
	id: string;
	sessionName: string;
	student: string;
	edits: {
		editor: string;
		editorName: string;
		content: string;
		timestamp: number;
	}[];
	createdAt: Date;
};

export type FlagLevels = {
	all: boolean;
	minor: boolean;
	major: boolean;
	severe: boolean;
};

const SwitchIcon = () => (
	<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M22.4857 17.2194C22.5554 17.289 22.6108 17.3717 22.6485 17.4628C22.6862 17.5538 22.7057 17.6514 22.7057 17.75C22.7057 17.8486 22.6862 17.9461 22.6485 18.0372C22.6108 18.1282 22.5554 18.211 22.4857 18.2806L20.2357 20.5306C20.095 20.6713 19.9041 20.7504 19.7051 20.7504C19.5061 20.7504 19.3152 20.6713 19.1745 20.5306C19.0337 20.3899 18.9547 20.199 18.9547 20C18.9547 19.801 19.0337 19.6101 19.1745 19.4694L20.1448 18.5H15.9551C15.8357 18.5 15.718 18.4715 15.6119 18.4169C15.5057 18.3622 15.4142 18.2831 15.3448 18.1859L8.06883 7.99999H3.20508C3.00617 7.99999 2.8154 7.92097 2.67475 7.78032C2.5341 7.63967 2.45508 7.4489 2.45508 7.24999C2.45508 7.05108 2.5341 6.86031 2.67475 6.71966C2.8154 6.57901 3.00617 6.49999 3.20508 6.49999H8.45508C8.57446 6.49999 8.69212 6.52848 8.79827 6.58311C8.90442 6.63773 8.996 6.71691 9.06539 6.81405L16.3413 17H20.1448L19.1745 16.0306C19.0337 15.8899 18.9547 15.699 18.9547 15.5C18.9547 15.301 19.0337 15.1101 19.1745 14.9694C19.3152 14.8286 19.5061 14.7496 19.7051 14.7496C19.9041 14.7496 20.095 14.8286 20.2357 14.9694L22.4857 17.2194ZM13.6113 10.5312C13.6915 10.5885 13.7821 10.6294 13.8781 10.6516C13.974 10.6738 14.0734 10.6769 14.1706 10.6607C14.2677 10.6446 14.3607 10.6094 14.4443 10.5572C14.5279 10.5051 14.6003 10.437 14.6576 10.3569L16.3451 8.00186H20.1485L19.1745 8.96936C19.0337 9.11009 18.9547 9.30097 18.9547 9.49999C18.9547 9.69901 19.0337 9.88988 19.1745 10.0306C19.3152 10.1713 19.5061 10.2504 19.7051 10.2504C19.9041 10.2504 20.095 10.1713 20.2357 10.0306L22.4857 7.78062C22.5554 7.71096 22.6108 7.62824 22.6485 7.53719C22.6862 7.44615 22.7057 7.34855 22.7057 7.24999C22.7057 7.15143 22.6862 7.05383 22.6485 6.96278C22.6108 6.87174 22.5554 6.78902 22.4857 6.71936L20.2357 4.46936C20.095 4.32863 19.9041 4.24957 19.7051 4.24957C19.5061 4.24957 19.3152 4.32863 19.1745 4.46936C19.0337 4.6101 18.9547 4.80097 18.9547 4.99999C18.9547 5.19901 19.0337 5.38988 19.1745 5.53061L20.1448 6.49999H15.9551C15.8357 6.49999 15.718 6.52848 15.6119 6.58311C15.5057 6.63773 15.4142 6.71691 15.3448 6.81405L13.4379 9.48311C13.3803 9.56327 13.3391 9.654 13.3166 9.75011C13.2942 9.84621 13.2909 9.94581 13.307 10.0432C13.3232 10.1406 13.3583 10.2338 13.4105 10.3175C13.4628 10.4013 13.531 10.4739 13.6113 10.5312ZM10.7988 14.4687C10.7187 14.4115 10.628 14.3706 10.5321 14.3484C10.4361 14.3261 10.3367 14.323 10.2396 14.3392C10.1424 14.3554 10.0494 14.3906 9.96586 14.4427C9.8823 14.4949 9.80982 14.563 9.75258 14.6431L8.06883 17H3.20508C3.00617 17 2.8154 17.079 2.67475 17.2197C2.5341 17.3603 2.45508 17.5511 2.45508 17.75C2.45508 17.9489 2.5341 18.1397 2.67475 18.2803C2.8154 18.421 3.00617 18.5 3.20508 18.5H8.45508C8.57446 18.5 8.69212 18.4715 8.79827 18.4169C8.90442 18.3622 8.996 18.2831 9.06539 18.1859L10.9723 15.5169C11.0299 15.4367 11.0711 15.346 11.0935 15.2499C11.116 15.1538 11.1192 15.0542 11.1031 14.9568C11.087 14.8594 11.0518 14.7662 10.9996 14.6824C10.9474 14.5987 10.8792 14.5261 10.7988 14.4687Z"
			fill="inherit"
		/>
	</svg>
);

// #region MAIN
export const TestResultsWrapper = () => {
	// #region STATEFUL VARIABLES
	const [assessmentInfo, setAssessmentInfo] = useState<QuizInfoSummary | null>(null);
	const [userSessionMap, setUserSessionMap] = useState<Map<SessionInfo["studentId"], SessionInfo[]> | null>(null);
	const [userNotesMap, setUserNotesMap] = useState<Map<string, LiveSessionNote[]> | null>(null);
	const [tabList, setTabList] = useState(["Results"]);
	const [studentUuid, setStudentUuid] = useState<string | undefined>();
	const { navigateToPage, changePortalContext } = usePortalStateContext();

	const [requireDownload, setRequireDownload] = useState(false);
	const [requireAut, setRequireAut] = useState(false);

	//for opening student report
	const [selectedStudent, setSelectedStudent] = useState<number>();
	const [showStudentResults, setShowStudentResults] = useState(false);
	const [sessionList, setSessionList] = useState<SessionInfo[]>();
	const [liveNotes, setLiveNotes] = useState<LiveSessionNote[] | undefined>();

	const [totalStudents, setTotalStudent] = useState<number>();
	const [completeCount, setCompletedCount] = useState<number>();
	const [incompleteCount, setIncompleteCount] = useState<number>();
	const [unavailableCount, setUnavailableCount] = useState<number>();
	const [pendingCount, setPendingCount] = useState<number>();
	const [errorCount, setErrorCount] = useState<number>();
	const [severeFlagCnt, setSevereFlagCnt] = useState<number>();
	const [majorFlagCnt, setMajorFlagCnt] = useState<number>();
	const [minorFlagCnt, setMinorFlagCnt] = useState<number>();
	const [noneFlagCnt, setNoneFlagCnt] = useState<number>();
	const [flagLevelFilter] = useState<FlagLevels>({
		all: true,
		minor: false,
		major: false,
		severe: false,
	});
	const [tableData, setTableData] = useState<UserRowData[]>([]); // Export button needs to know table data

	/* Determine if we should render a request for Course & Quiz ID */
	const [needInformation, setNeedInformation] = useState<boolean>(false);
	const [instituteId, setInstituteId] = useState<number>(NaN);
	const [courseId, setCourseId] = useState<number>(NaN);
	const [quizId, setQuizId] = useState<string>("");
	const [initialProctoringSessionId, setInitialProctoringSessionId] = useState<string | undefined>(undefined);

	/* Course / Quiz Selector Modal State */
	const [openCourseSelector, setOpenCourseSelector] = useState<boolean>(false);
	const [openQuizSelector, setOpenQuizSelector] = useState<boolean>(false);

	registerPlugin();

	// #endregion

	// #region COMPONENT LOGIC

	const setLabelValues = (usermap?: typeof userSessionMap) => {
		let noOfStudents = 0;
		let totalSevereFlag = 0;
		let totalMajorFlag = 0;
		let totalMinorFlag = 0;
		let totalNoneFlag = 0;
		let completeCount = 0;
		let incompleteCount = 0;
		let unavailableCount = 0;
		let pendingCount = 0;
		let errorCount = 0;

		if (usermap) {
			for (const user of usermap.values()) {
				noOfStudents++;
				let maxFlagLevel = 0;
				let hasCompleteSession = false;
				let hasIncompleteSession = false;
				let hasUnavailableSession = false;
				let hasPendingSession = false;
				let hasError = false;
				for (const session of user) {
					if (typeof session?.["flagLevel"] === "number") {
						if ((session["flagLevel"] ?? 0) > maxFlagLevel) {
							maxFlagLevel = session["flagLevel"];
						}

						if (session["status"] != null && session["status"] != undefined) {
							if (session["status"] == "flagged" || session["status"] == "unflagged") {
								hasCompleteSession = true;
							} else if (session["status"] == "incomplete") {
								hasIncompleteSession = true;
							} else if (session["status"] == "unavailable") {
								hasUnavailableSession = true;
							} else if (session["status"] == "pending") {
								hasPendingSession = true;
							} else {
								hasError = true;
							}
						}
					}
				}

				if (maxFlagLevel === 3) {
					totalSevereFlag++;
				} else if (maxFlagLevel === 2) {
					totalMajorFlag++;
				} else if (maxFlagLevel === 1) {
					totalMinorFlag++;
				} else {
					totalNoneFlag++;
				}

				if (hasCompleteSession) {
					completeCount++;
				} else if (hasIncompleteSession) {
					incompleteCount++;
				} else if (hasUnavailableSession) {
					unavailableCount++;
				} else if (hasPendingSession) {
					pendingCount++;
				} else if (hasError) {
					errorCount++;
				}
			}
		}

		setSevereFlagCnt(totalSevereFlag);
		setMajorFlagCnt(totalMajorFlag);
		setMinorFlagCnt(totalMinorFlag);
		setNoneFlagCnt(totalNoneFlag);
		setTotalStudent(noOfStudents);
		setCompletedCount(completeCount);
		setIncompleteCount(incompleteCount);
		setUnavailableCount(unavailableCount);
		setPendingCount(pendingCount);
		setErrorCount(errorCount);
	};

	// Could want to have this in the useLayoutEffect
	const convertSessionList = (sessionList: SessionInfo[]) => {
		// Storing sessions for each student (each session is technically an "attempt" for the quiz)
		// Creating a usermap that associates each session with the studentId
		// The current data structure is a Map that maps each studentId to an array of their sessions
		// Could potentially be improved by having the array instead be a map that maps each session id to the session info
		// This could potentially save client side processing time (one example would be in the "data = useMemo..." section of the SelectionMenu)
		const userMap: typeof userSessionMap = new Map();
		if (sessionList) {
			for (const session of sessionList) {
				const sessionInfos = userMap.get(session.studentId) ?? [];
				sessionInfos.push(session);
				userMap.set(session.studentId, sessionInfos);
			}
		}
		setUserSessionMap(userMap);
		setLabelValues(userMap);
	};

	const convertSessionNotes = (
		sessionNotes: {
			id: string;
			name: string;
			liveSessionNotes: LiveSessionNote[];
		}[],
	) => {
		const notesMap: typeof userNotesMap = new Map();
		for (const liveSession of sessionNotes) {
			for (const note of liveSession.liveSessionNotes) {
				const studentNotes = notesMap.get(note.student) ?? [];
				studentNotes.push(note);
				notesMap.set(note.student, studentNotes);
			}
		}
		setUserNotesMap(notesMap);
	};

	// Sample code for dynamically creating a new tab on the front end which is achieved by adding a tab to the tablist
	// const openPlaybackTab = (name) => {
	//     if (tabList.length === 1) {
	//         const newTabList = tabList;
	//         newTabList.push(`YuJa Playback - ${name}`);
	//         setTabList(newTabList);
	//     }
	//     else {
	//         const newTabList = [];
	//         newTabList.push(tabList[0]);
	//         newTabList.push(`YuJa Playback - ${name}`);
	//         setTabList(newTabList);
	//     }
	//     setSelectedTab(1);
	// }

	// #endregion

	// #region SESSION GETTERS
	const getSessionList = async (courseIdParam?: number, assessmentId?: string) => {
		try {
			if (!courseIdParam || !Number.isInteger(courseIdParam)) {
				// FIXME better error handling
				throw new Error("Missing course");
			}
			// Whether or not assessmentId exists determined whether to get list of sessions for all quizzes in course, or for just the selected quiz
			const [listResponse, notesResponse] = await Promise.all(
				assessmentId
					? [
							apiClient.proctoringSession.getSessionResultList(assessmentId, courseIdParam),
							(async () => {
								const course = await apiClient.course.getCourseInfoFromId(courseIdParam);
								const institute = course?.instituteId ? await apiClient.institute.get(course.instituteId) : undefined;
								const assessment = await apiClient.assessment.getAssessmentByCourseQuizId({
									courseId: course.id,
									lmsQuizId: assessmentId,
								});
								if (assessment) {
									if (
										typeof assessment.quizInfo === "object" &&
										assessment.quizInfo !== null &&
										"name" in assessment.quizInfo
									) {
										await changePortalContext({
											courseId: course.id,
											assessmentId: assessment.id,
										});
										navigateToPage("assessmentReport");
										setAssessmentInfo({
											id: assessment.quizId,
											name:
												assessment.quizInfo.name ??
												i18n.t("result.components.resultsPage.testResultsWrapperTSX.unamedAssessment"),
										});
									} else {
										setAssessmentInfo({
											id: assessment.quizId,
											name: i18n.t("result.components.resultsPage.testResultsWrapperTSX.unamedAssessment"),
										});
									}
								}
								if (!institute || !course || !assessment) {
									return [];
								}
								const liveSessions = await apiClient.liveClient
									.getAssessmentLiveSessionInfo({
										instituteUuid: institute.uuid,
										courseUuid: course.accessControlUuid,
										assessmentUuid: intIdToUuid(assessment.id),
									})
									.catch(() => {
										console.error("Failed to get live session info");
										return { results: [] };
									});
								const liveSessionsWithNotes = await Promise.all(
									liveSessions.results.map(async (liveSession) => {
										let proctorGroupId: number | undefined;
										let proctorGroupName: string | undefined;
										try {
											const invitations = await apiClient.liveClient.getLiveSessionInvitations(liveSession.id);
											if (invitations.length > 0) {
												proctorGroupId = invitations[0].proctorGroupId;
												proctorGroupName = (await apiClient.liveClient.getLiveProctorGroup(proctorGroupId)).name;
											}
										} catch (e) {
											console.error(e);
										}
										return {
											...liveSession,
											liveSessionNotes: await apiClient.liveClient.getLiveSessionNotes(liveSession.id),
											proctorGroupId,
											proctorGroupName,
										};
									}),
								).catch(() => {
									console.error("Failed to get live session notes");
									return [];
								});
								const allEditorIds = [
									...new Set(
										liveSessionsWithNotes
											.filter(({ proctorGroupId }) => proctorGroupId == null)
											.flatMap(({ liveSessionNotes }) =>
												liveSessionNotes.flatMap(({ edits }) => edits.map(({ editor }) => editor)),
											),
									),
								];
								const editorNames: Record<string, string> = Object.fromEntries(
									await Promise.all(
										allEditorIds.map(async (editorId) => {
											try {
												return [editorId, (await apiClient.user.getAccessControlUserInfo(editorId)).name];
											} catch {
												return [editorId, undefined];
											}
										}),
									),
								);
								return liveSessionsWithNotes.map((session) => ({
									...session,
									liveSessionNotes: session.liveSessionNotes
										.filter((note) => note.edits.length > 0)
										.map((note) => ({
											...note,
											sessionName: session.name,
											edits: note.edits.map((edit) => ({
												...edit,
												editorName:
													(session.proctorGroupId == null ? editorNames[edit.editor] : session.proctorGroupName) ?? "",
											})),
											createdAt: new Date(note.edits[0].timestamp),
										})),
								}));
							})(),
						]
					: [apiClient.proctoringSession.getSessionList(courseIdParam), null],
			);
			const urlSearch = new URLSearchParams(window.location.search);
			const studentUserId = urlSearch.get("userId") ?? selectedStudent;
			const studentInfo =
				studentUserId && !isNaN(Number(studentUserId))
					? await apiClient.user.getUserInfo(Number(studentUserId))
					: undefined;
			setStudentUuid(studentInfo?.uuid);

			convertSessionList(listResponse ?? []);
			if (notesResponse && notesResponse.reduce((a, b) => a + b.liveSessionNotes.length, 0) > 0) {
				convertSessionNotes(notesResponse);
				const newTabList = tabList;
				newTabList[1] = "Live Session Notes";
				setTabList(newTabList);
				const noteAuthorIds = new Set(
					notesResponse.flatMap((session) =>
						session.liveSessionNotes.flatMap((note) =>
							note.edits.filter(({ editorName }) => !editorName).map(({ editor }) => editor),
						),
					),
				);
				const userInfoResponses = await Promise.allSettled(
					[...noteAuthorIds].map((userId) => apiClient.user.getAccessControlUserInfo(userId)),
				);
				const userNameMap = new Map<string, string>();
				userInfoResponses.forEach((response) => {
					if (response.status === "fulfilled") {
						const { value } = response;
						userNameMap.set(value.id, value.name);
					}
				});
			}
		} catch (err) {
			console.error(err);
			setRequireAut(true);
		}
	};

	const getAndSetFirstActiveAssessment = async (courseIdParam: number) => {
		try {
			const assessments = await apiClient.assessment.getAssessments({ course: { id: courseIdParam } });
			const firstActiveAssessment = assessments.find((assessment) => !assessment.isDeleted);

			if (firstActiveAssessment && firstActiveAssessment.quizId) {
				const url = new URL(window.location.href);
				url.searchParams.set("lmsQuizId", firstActiveAssessment.quizId);
				window.history.replaceState(window.history.state, "", url);

				await getSessionList(courseIdParam, firstActiveAssessment.quizId);
			} else {
				setNeedInformation(true);
			}
		} catch (err) {
			console.error(err);
			/* Incase storage fails, request manual input of course and/or quiz */
			setNeedInformation(true);
		}
	};

	// Node server cannot get the quiz Id from LTI requests, so we request the extension to give us the lms quiz id (for Canvas only).
	const getSessionListUsingExtension = async (courseIdParam: number) =>
		new Promise((res, rej) => {
			const timeout = setTimeout(() => rej("Extension quiz info message timed out."), 2000);
			if (window.chrome) {
				chrome.runtime.sendMessage(
					import.meta.env.VITE_EXTENSION_ID,
					{ request: "canvas-quiz-menu-lti-quiz-info" },
					function (response: unknown) {
						if (chrome?.runtime?.lastError) {
							rej("Send message error: " + chrome.runtime.lastError.message);
							return;
						}
						clearTimeout(timeout);
						if (
							typeof response === "object" &&
							response &&
							"quizId" in response &&
							typeof response.quizId === "string"
						) {
							const url = new URL(window.location.href);
							url.searchParams.set("lmsQuizId", response.quizId);
							window.history.replaceState(window.history.state, "", url);

							getSessionList(courseIdParam, response.quizId).catch(console.error);
							res(true);
							return;
						}
						res(false);
					},
				);
			} else {
				dispatchProxyMessage({ request: "canvas-quiz-menu-lti-quiz-info" })
					.then((response: unknown) => {
						if (
							typeof response === "object" &&
							response &&
							"quizId" in response &&
							typeof response.quizId === "string"
						) {
							const url = new URL(window.location.href);
							url.searchParams.set("lmsQuizId", response.quizId);
							window.history.replaceState(window.history.state, "", url);

							getSessionList(courseIdParam, response.quizId).catch(console.error);
							res(true);
						} else {
							res(false);
						}
					})
					.catch((err) => {
						console.error(err);
						res(false);
					});
			}
		}).catch((err) => {
			console.error(err);
			setRequireDownload(true);
		});

	/* Blackboard Ultra direct link to results page handler */
	const getSessionListUsingStorage = async (instituteIdParam: number, courseIdParam: number) => {
		const quizInfo = storage.VerityQuizInfo.get();

		if (quizInfo?.quizId && quizInfo.courseId && instituteIdParam && Number.isInteger(instituteIdParam)) {
			const courseInfo = await apiClient.course.getCourseInfoFromLmsCourseId({
				lmsCourseId: quizInfo.courseId,
				instituteId: instituteIdParam,
			});
			setCourseId(courseInfo.id);

			const url = new URL(window.location.href);
			url.searchParams.delete("instituteId");
			url.searchParams.set("course", courseInfo.id.toString());
			url.searchParams.set("lmsQuizId", quizInfo.quizId);
			window.history.replaceState(window.history.state, "", url);
			console.error(courseInfo.id, quizInfo.quizId);
			await getSessionList(courseInfo.id, quizInfo.quizId);
		} else {
			await getAndSetFirstActiveAssessment(courseIdParam);
		}
	};

	// #endregion

	// #region REACT EFFECTS
	useEffect(() => {
		// Parse through the search params where we attach the course and quiz id.
		const fetchData = async () => {
			const params = new URLSearchParams(window.location.search);
			const sessionIdParam = params.get("sessionId") ? params.get("sessionId") : undefined;
			const instituteIdParam = params.get("instituteId") ? Number(params.get("instituteId")) : NaN;
			let lmsQuizIdParam = params.get("lmsQuizId");
			setInstituteId(instituteIdParam);
			let courseIdParam = params.get("course") ? Number(params.get("course")) : NaN;
			const quizIdParam = params.get("quizId");
			if (sessionIdParam) {
				try {
					const { studentId } = await apiClient.proctoringSession.get(sessionIdParam);
					const { assessment, course, institute } = await apiClient.proctoringSession.getLmsContext(sessionIdParam);
					lmsQuizIdParam = assessment.quizId;
					courseIdParam = course.id;

					setInstituteId(institute.id);
					setInitialProctoringSessionId(sessionIdParam);
					setSelectedStudent(studentId);
					setShowStudentResults(true);
				} catch (e) {
					console.error(e);
					setRequireAut(true);
				}
			} else if (quizIdParam) {
				const assessment = await apiClient.assessment.getAssessmentByAssessmentId(Number(quizIdParam));
				if (assessment) {
					lmsQuizIdParam = assessment.quizId;
					courseIdParam = assessment.courseId;
				}
			}
			if (!courseIdParam) {
				const lmsCourseIdParam = params.get("lmsCourseId") ? params.get("lmsCourseId") : undefined;
				if (lmsCourseIdParam) {
					courseIdParam = await apiClient.course
						.getCourseInfoFromLmsCourseId({
							lmsCourseId: lmsCourseIdParam,
							instituteId: instituteIdParam,
						})
						.then((course) => course.id);
					if (!lmsQuizIdParam && courseIdParam) {
						lmsQuizIdParam = "storage";
					}
				}
			}
			setCourseId(courseIdParam);
			if (lmsQuizIdParam) {
				if (lmsQuizIdParam === "extension") {
					await getSessionListUsingExtension(courseIdParam);
				} else if (lmsQuizIdParam === "storage") {
					await getSessionListUsingStorage(instituteIdParam, courseIdParam);
				} else {
					await getSessionList(courseIdParam, lmsQuizIdParam);
				}
			} else {
				await getAndSetFirstActiveAssessment(courseIdParam);
			}
		};
		fetchData().catch(console.error);
	}, []);

	/* Handler for when we need more information from the User */
	useEffect(() => {
		/* If we require information, wait until we have values for Course & Quiz then refresh the page */
		if (!Number.isNaN(courseId) && !(quizId === "")) {
			const url = new URL(window.location.href);
			url.searchParams.set("course", courseId.toString());
			url.searchParams.set("lmsQuizId", quizId);
			url.searchParams.delete("quizId");
			setCompletedCount(undefined);
			setSevereFlagCnt(undefined);
			setMajorFlagCnt(undefined);
			setMinorFlagCnt(undefined);
			setNoneFlagCnt(undefined);
			setTotalStudent(undefined);
			setAssessmentInfo(null);
			setUserSessionMap(null);
			window.history.replaceState(window.history.state, "", url);
			getSessionList(courseId, quizId.toString()).catch(() => setRequireAut(true));
			setNeedInformation(false);
			return;
		}
	}, [courseId, quizId]);

	useEffect(() => {
		const debounceTimeout = setTimeout(async () => {
			if (userSessionMap && selectedStudent !== undefined) {
				const sessions = userSessionMap.get(selectedStudent);
				if (sessions && sessions.length > 0) {
					setSessionList(sessions);
				}
				const studentInfo =
					selectedStudent && !isNaN(selectedStudent) ? await apiClient.user.getUserInfo(selectedStudent) : undefined;
				if (studentInfo) {
					setLiveNotes(userNotesMap?.get(studentInfo?.uuid));
				}
			} else {
				setSessionList(undefined);
				setLiveNotes(undefined);
			}
		}, 300); // Debounce to prevent multiple requests
		return () => clearTimeout(debounceTimeout);
	}, [selectedStudent, showStudentResults, userSessionMap, userNotesMap, apiClient]);

	// #endregion

	const studentList = Array.from(userSessionMap?.keys() ?? []).map((userId) => ({
		id: userId,
		name:
			userSessionMap?.get(userId)?.[0].userInfo?.full_name ??
			i18n.t("result.components.resultsPage.testResultsWrapperTSX.unknownStudent"),
		severityLevel: userSessionMap
			?.get(userId)
			?.map((session) => session.flagLevel ?? 0)
			.reduce((a, b) => Math.max(a, b), 0),
	}));
	// #region REDIRECT HANDLER
	useEffect(() => {
		const urlSearch = new URLSearchParams(window.location.search);
		const isRedirected = urlSearch.get("redirected");
		if (isRedirected) {
			const courseId = Number(urlSearch.get("course"));
			const sessionNum = urlSearch.get("sessionNum");
			const userId = urlSearch.get("userId");
			const selectedLiveNotes = studentUuid ? userNotesMap?.get(studentUuid) : undefined;
			const sessionList = userSessionMap?.get(Number(userId));

			if (Number.isInteger(courseId) && sessionNum && sessionList) {
				setCourseId(courseId);
				setLiveNotes(selectedLiveNotes);
				setSelectedStudent(Number(userId));
				setSessionList(sessionList);
				setShowStudentResults(true);
			}
		}
	}, [userSessionMap]);
	// #endregion
	// #region COMPONENT

	// Replace with FlexBox, text, ... generics upon merge of Scope test framework

	return (
		<>
			{needInformation && !requireAut && (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "safe center",
						flexWrap: "nowrap",
						height: "100%",
						minHeight: 0,
						backgroundColor: "#ffffff",
						overflow: "auto",
					}}
				>
					<Box sx={{ display: "flex", padding: "16px", marginBottom: "16px" }}>
						<Typography sx={{ fontSize: "32px", fontWeight: "bold" }}>
							{i18n.t(
								Number.isNaN(courseId)
									? "result.components.resultsPage.testResultsWrapperTSX.selectCoursePrompt"
									: "result.components.resultsPage.testResultsWrapperTSX.selectAssessmentPrompt",
							)}
						</Typography>
					</Box>

					<Box sx={{ display: "flex", padding: "32px" }}>
						<NoSelectionIcon />
					</Box>

					<Box sx={{ display: "flex", padding: "16px" }}>
						<CoreButton
							aria-label="Selector Button"
							onClick={() => (Number.isNaN(courseId) ? setOpenCourseSelector(true) : setOpenQuizSelector(true))}
							sx={{
								display: "flex",
								width: "auto",
								height: "35px",
								textWrap: "nowrap",
								justifyContent: "space-between",
							}}
						>
							<SwitchIcon />
							<Typography sx={{ padding: "8px", color: "inherit" }}>
								{i18n.t(
									Number.isNaN(courseId)
										? "result.components.resultsPage.testResultsWrapperTSX.course"
										: "result.components.resultsPage.testResultsWrapperTSX.assessment",
								)}
							</Typography>
						</CoreButton>
					</Box>
				</Box>
			)}

			{openCourseSelector && (
				<CourseSelectorModal
					instituteId={instituteId}
					open={openCourseSelector}
					setOpen={setOpenCourseSelector}
					setCourseId={setCourseId}
					setRequireAut={setRequireAut}
				></CourseSelectorModal>
			)}

			{openQuizSelector && (
				<QuizSelectorModal
					courseId={courseId}
					open={openQuizSelector}
					setOpen={setOpenQuizSelector}
					setQuizId={setQuizId}
					setRequireAut={setRequireAut}
				></QuizSelectorModal>
			)}

			{((!needInformation && !Number.isNaN(courseId) && !Number.isNaN(quizId)) || requireAut) && (
				<div
					style={{
						width: "100%",
						height: "100%",
						background: "#F7F8FA",
						overflow: "auto",
					}}
				>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "flex-start",
							width: "100%",
							maxWidth: "1600px",
							height: "auto",
							minHeight: "100%",
							margin: "0 auto",
						}}
					>
						{showStudentResults && selectedStudent && sessionList ? (
							<ProctoringViewTabsProvider>
								<ProctoringResultView
									courseId={courseId}
									showVideo={true}
									selectedLiveNotes={liveNotes}
									proctoringSessions={sessionList}
									setProctoringSessions={setSessionList}
									setShowStudentResults={setShowStudentResults}
									assessmentInfo={assessmentInfo}
									selectedStudent={selectedStudent}
									setSelectedStudent={setSelectedStudent}
									studentList={studentList}
									initialProctoringSessionId={initialProctoringSessionId}
								/>
							</ProctoringViewTabsProvider>
						) : (
							<Box display="flex" flexDirection="column" margin="10px" gap="14px" width="100%">
								<Box
									sx={{
										"&:empty": {
											display: "none",
										},
										"&:not(:empty)": {
											display: "flex",
											width: "100%",
											maxWidth: "100%",
											boxSizing: "border-box",
										},
									}}
								>
									<VisualizerBanner />
								</Box>
								<CoreCard>
									<Grid container spacing={1.6} justifyContent={"space-between"} alignItems={"center"} marginTop={0}>
										<Grid
											item
											container
											sm={"auto"}
											md={"auto"}
											lg={true}
											display="flex"
											flexDirection="column"
											gap="12px"
											sx={{
												maxWidth: { md: "100%", sm: "100%" }, // Adjusted for responsive wrapping
												wordBreak: "break-word", // Ensures long text wraps
											}}
											paddingTop={"0 !important"}
										>
											<Typography
												variant="h1"
												fontWeight={500}
												fontSize={"20px"}
												color="black"
												whiteSpace="normal"
												textOverflow="ellipsis"
												textAlign="left"
											>
												{i18n.t(
													assessmentInfo
														? "result.components.resultsPage.testResultsWrapperTSX.assessmentResultWithName"
														: "result.components.resultsPage.testResultsWrapperTSX.assessmentResult",
													{ name: assessmentInfo?.name?.replace(" (YuJa Verity for Test Proctoring)", "") },
												)}
											</Typography>
											<Typography
												color={"#6b6b6b"}
												fontWeight={400}
												fontSize={"15px"}
												whiteSpace="normal"
												textOverflow="ellipsis"
											>
												{i18n.t("result.components.resultsPage.testResultsWrapperTSX.overviewMsg")}
											</Typography>
										</Grid>
										<Grid
											item
											container
											sm={"auto"}
											md={"auto"}
											lg={"auto"}
											sx={{ overflow: "hidden" }}
											height={"max-content"}
											gap={"10px"}
											padding={"5px !important"}
										>
											<CoreButton
												onClick={() => {
													setOpenQuizSelector(true);
												}}
												sx={{
													height: "auto",
													marginTop: "1px",
													maxHeight: "100%",
													overflow: "hidden",
												}}
												startIcon={<SwitchIcon />}
												aria-haspopup="dialog"
											>
												{i18n.t("result.components.resultsPage.testResultsWrapperTSX.switchAssessment")}
											</CoreButton>
											<ExportReportButton
												reportInfo={{
													assessmentReportInfo: {
														severityCounts: {
															none: noneFlagCnt ?? 0,
															minor: minorFlagCnt ?? 0,
															major: majorFlagCnt ?? 0,
															severe: severeFlagCnt ?? 0,
														},
														completionCount: {
															complete: completeCount ?? 0,
															incomplete: incompleteCount ?? 0,
															unavailable: unavailableCount ?? 0,
															pending: pendingCount ?? 0,
															error: errorCount ?? 0,
														},
														testTakerTableReport: tableData,
														students: {
															total: totalStudents ?? 0,
															month: 0, // To satisfy typescript
														},
														assessmentName: assessmentInfo?.name ?? "",
													},
													reportType: "assessment",
												}}
											/>
										</Grid>
									</Grid>
								</CoreCard>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: {
											s: "repeat(1, 1fr)", // For extra small screens (1 column)
											md: "repeat(2, 1fr)", // For small screens (2 columns)
											lg: "repeat(4, 1fr)", // For medium and larger screens (4 columns)
										},
										gridTemplateRows: "auto", // You can adjust rows similarly if needed
										columnGap: "40px",
										rowGap: "15px",
									}}
								>
									<SummaryValueCard
										title={i18n.t("result.components.resultsPage.testResultsWrapperTSX.totalAssessmentCompleted")}
										text={
											completeCount !== undefined && totalStudents !== undefined
												? `${completeCount}/${totalStudents}`
												: undefined
										}
										color="rgba(31, 109, 212, 15%)"
										headerIcon={<StudentsIcon />}
									/>
									<SummaryValueCard
										title={i18n.t("result.components.resultsPage.testResultsWrapperTSX.severeFlaggedUsers")}
										number={severeFlagCnt}
										color="rgba(180, 0, 48, 15%)"
										headerIcon={<SevereFlag />}
									/>
									<SummaryValueCard
										title={i18n.t("result.components.resultsPage.testResultsWrapperTSX.majorFlaggedUsers")}
										number={majorFlagCnt}
										color="rgba(255, 119, 0, 15%)"
										headerIcon={<MajorFlag />}
									/>
									<SummaryValueCard
										title={i18n.t("result.components.resultsPage.testResultsWrapperTSX.minorFlaggedUsers")}
										number={minorFlagCnt}
										color="rgba(0, 132, 0, 15%)"
										headerIcon={<MinorFlag />}
									/>
								</Box>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: {
											s: "repeat(1, 1fr)", // For extra small screens (1 column)
											md: "repeat(2, 1fr)", // For small screens (2 columns)
										},
										gridTemplateRows: "auto", // You can adjust rows similarly if needed
										columnGap: "40px",
										rowGap: "15px",
									}}
								>
									<AssessmentCompletionChart
										completionCounts={
											completeCount !== undefined &&
											incompleteCount !== undefined &&
											unavailableCount !== undefined &&
											pendingCount !== undefined &&
											errorCount !== undefined
												? {
														complete: completeCount,
														incomplete: incompleteCount,
														unavailable: unavailableCount,
														pending: pendingCount,
														error: errorCount,
													}
												: undefined
										}
									/>
									<AssessmentSeverityChart
										severityCounts={
											severeFlagCnt !== undefined || majorFlagCnt !== undefined || minorFlagCnt !== undefined
												? {
														severe: severeFlagCnt ?? 0,
														major: majorFlagCnt ?? 0,
														minor: minorFlagCnt ?? 0,
														none: noneFlagCnt ?? 0,
													}
												: undefined
										}
									/>
								</Box>
								<SelectionMenu
									courseId={courseId}
									userIdLiveNotesMap={userNotesMap}
									userIdProctoringSessionMap={userSessionMap}
									requireDownload={requireDownload}
									requireAut={requireAut}
									flagLevelFilter={flagLevelFilter}
									setSelectedStudent={setSelectedStudent}
									setShowStudentResults={setShowStudentResults}
									setTableData={setTableData}
								/>
							</Box>
						)}
					</div>
				</div>
			)}
		</>
	);
};
// #endregion
// #endregion

// #region COURSE SELECTOR
/* Prop requirements for the course selector */
type CourseSelectorModalProps = {
	instituteId: number;
	open: boolean;
	setOpen: (id: boolean) => void;
	setCourseId: (id: number) => void;
	setRequireAut: (value: boolean) => void;
};

// TODO: Create new components in packages for course / institution / assessment table display
// TODO: Currently fragmented API calls in a few places

/* Modal to select a course when we require more information */
const CourseSelectorModal = ({ instituteId, open, setOpen, setCourseId, setRequireAut }: CourseSelectorModalProps) => {
	/* Stateful variable definition */
	const [allCourses, setAllCourses] = useState<CourseInfoSummary[]>([]);
	const [searchFilter, setSearchFilter] = useState("");
	const [selectedCourse, setSelectedCourse] = useState<number>(NaN);
	const [loading, setLoading] = useState<boolean>(false);

	const filteredData = allCourses.filter(
		(s) => !searchFilter || s.name?.toLowerCase().includes(searchFilter.toLowerCase()),
	);
	const controlId = useId();

	/* Initial fetch for course data */
	useEffect(() => {
		async function fetchCoursesData() {
			try {
				setLoading(true);
				const totalCoursesInfo = await apiClient.course.getInstituteCoursesInfo(instituteId);
				const courseSummaries: CourseInfoSummary[] = totalCoursesInfo.map((course) => ({
					id: course.id,
					name: course.name,
				}));
				setAllCourses(courseSummaries);
				setLoading(false);
			} catch (e) {
				setRequireAut(true);
				setLoading(false);
			}
		}

		if (!Number.isNaN(instituteId)) {
			void fetchCoursesData();
		} else {
			setRequireAut(true);
		}
	}, [instituteId]);

	/* Table column definition */
	const columns: CoreTableColumn<CourseInfoSummary>[] = [
		{
			header: i18n.t("result.components.resultsPage.testResultsWrapperTSX.courseID"),
			key: "course-id",
			width: "20%",
			truncateString: true,
			descendingComparator: (a, b) => (a.id! < b.id! ? 1 : -1),
			cell: (row) => <div>{row.data.id}</div>,
			align: "left",
			padding: "10px 10px 10px 15px",
		},
		{
			header: i18n.t("result.components.resultsPage.testResultsWrapperTSX.courseName"),
			key: "course-name",
			width: "80%",
			truncateString: true,
			cell: (row) => <div>{row.data.name}</div>,
			descendingComparator: (a, b) => (a?.name ?? "").toLowerCase().localeCompare((b?.name ?? "").toLowerCase()),
			align: "left",
			padding: "10px 10px 10px 15px",
		},
	];

	/* Component return */
	return (
		<>
			{/* Modal Container */}
			<CoreModal
				title={i18n.t("result.components.resultsPage.testResultsWrapperTSX.selectCourse")}
				showTopCloseButton={true}
				open={open}
				minWidth="max(50%,360px)"
				widthFixed={true}
				onClose={() => setOpen(false)}
				buttons={[
					{
						text: i18n.t("result.components.resultsPage.testResultsWrapperTSX.confirm"),
						color: "primary",
						onClick: () => {
							setCourseId(selectedCourse);
							setOpen(false);
						},
						disabled: Number.isNaN(selectedCourse),
					},
				]}
			>
				{/* Modal Body Container */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexDirection: "column",
						padding: "16px",
						gap: "15px",
					}}
				>
					<CoreSearchFieldPaper>
						<CoreSearchField
							onSearch={(value) => setSearchFilter(value)}
							ariaControls={controlId}
							totalResults={filteredData.length}
							label={i18n.t("result.components.resultsPage.testResultsWrapperTSX.searchForCourse")}
							showSearchButton
							value={searchFilter}
						/>
					</CoreSearchFieldPaper>

					{/* Course Table */}
					<CoreTable
						id={controlId}
						data={filteredData}
						columns={columns}
						fontSize={16}
						headerFontSize={16}
						paginationSize="medium"
						rowsPerPage={7}
						padding={"16px"}
						rowHeight={60}
						hover={true}
						loading={loading}
						useSkeleton={true}
						noResultsString={i18n.t("result.components.resultsPage.testResultsWrapperTSX.noCoursesFound")}
						rowProps={(row: CoreTableRow<CourseInfoSummary>) => ({
							selected: selectedCourse === row.data.id,
						})}
						onRowClick={(row: CoreTableRow<CourseInfoSummary>) => {
							if (row.data && row.data.id) {
								setSelectedCourse(row.data.id);
							} else {
								setSelectedCourse(NaN);
							}
						}}
					/>
				</Box>
			</CoreModal>
		</>
	);
};
// #endregion

// #region QUIZ SELECTOR
/* Prop requirements for the quiz selector */
type QuizSelectorModalProps = {
	courseId: number;
	open: boolean;
	setOpen: (id: boolean) => void;
	setQuizId: (id: string) => void;
	setRequireAut: (value: boolean) => void;
};

export type QuizInfoSummary = {
	id?: string | null;
	name?: string | null;
};

/* Modal to select a quiz when we require more information */
const QuizSelectorModal = ({ courseId, open, setOpen, setQuizId, setRequireAut }: QuizSelectorModalProps) => {
	/* Stateful variable definition */
	const [allQuizzes, setAllQuizzes] = useState<QuizInfoSummary[]>([]);
	const [searchFilter, setSearchFilter] = useState("");
	const [loading, setLoading] = useState<boolean>(true);

	const filteredData = allQuizzes.filter(
		(s) => !searchFilter || s.name?.toLowerCase().includes(searchFilter.toLowerCase()),
	);

	const controlId = useId();
	/* Initial fetch for course data */
	useEffect(() => {
		async function fetchQuizzesData() {
			try {
				setLoading(true);
				const totalQuizzesInfo = await apiClient.assessment.getAssessments({ course: { id: courseId } });

				const quizSummaries: QuizInfoSummary[] = totalQuizzesInfo
					.filter((quiz) => !quiz.isDeleted)
					.map((quiz) => ({
						id: quiz.quizId,
						name: quiz.quizInfo
							? typeof quiz.quizInfo === "string"
								? quiz.quizInfo
								: quiz.quizInfo.name
							: i18n.t("result.components.resultsPage.testResultsWrapperTSX.unamedAssessment"),
					}));

				setAllQuizzes(quizSummaries);
				setLoading(false);
			} catch (e) {
				setRequireAut(true);
				setLoading(false);
			}
		}

		if (!Number.isNaN(courseId)) {
			void fetchQuizzesData();
		} else {
			setRequireAut(true);
		}
	}, [courseId]);

	/* Table column definition */
	const columns: CoreTableColumn<QuizInfoSummary>[] = [
		{
			header: i18n.t("result.components.resultsPage.testResultsWrapperTSX.assessmentID"),
			key: "quiz-id",
			width: "20%",
			truncateString: true,
			showTooltip: true,
			descendingComparator: (a, b) => (a.id! < b.id! ? 1 : -1),
			cell: (row) => row.data.id,
			align: "left",
			padding: "10px 10px 10px 15px",
		},
		{
			header: i18n.t("result.components.resultsPage.testResultsWrapperTSX.assessmentName"),
			key: "quiz-name",
			width: "80%",
			truncateString: true,
			showTooltip: true,
			cell: (row) => row.data.name,
			descendingComparator: (a, b) => (a?.name ?? "").toLowerCase().localeCompare((b?.name ?? "").toLowerCase()),
			align: "left",
			padding: "10px 10px 10px 15px",
		},
	];

	/* Component Return */
	return (
		<>
			{/* Modal Container */}
			<CoreModal
				title={i18n.t("result.components.resultsPage.testResultsWrapperTSX.selectAssessment")}
				showTopCloseButton={true}
				open={open}
				minWidth="min(90%, 600px)"
				widthFixed={true}
				onClose={() => setOpen(false)}
				showBottomCloseButton={true}
			>
				{/* Modal Body Container */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexDirection: "column",
						padding: "16px",
						gap: "15px",
					}}
				>
					<CoreSearchFieldPaper sx={{ padding: "10px" }}>
						<CoreSearchField
							onSearch={(value) => setSearchFilter(value)}
							label={i18n.t("result.components.resultsPage.testResultsWrapperTSX.searchForAssessment")}
							ariaControls={controlId}
							totalResults={filteredData.length}
							showSearchButton
							value={searchFilter}
						/>
					</CoreSearchFieldPaper>

					<CoreTable
						id={controlId}
						data={filteredData}
						columns={columns}
						fontSize={16}
						headerFontSize={16}
						paginationSize="medium"
						rowsPerPage={7}
						padding={"16px"}
						rowHeight={60}
						loading={loading}
						useSkeleton={true}
						hover={true}
						allowUIOverrides
						noResultsString={i18n.t("result.components.resultsPage.testResultsWrapperTSX.noAsessmentsFound")}
						onRowClick={(row: CoreTableRow<QuizInfoSummary>) => {
							if (row.data && row.data.id) {
								setQuizId(row.data.id);
								setOpen(false);
							}
						}}
					/>
				</Box>
			</CoreModal>
		</>
	);
};
// #endregion

export default TestResultsWrapper;
